import { ampli } from 'lib-shared-tracking';
import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { createKeybindingsHandler, KeyBindingMap } from 'tinykeys';

import {
  CommandPubSubEvent,
  commandPubSubEventNamePrefix,
  useCommands,
} from 'utils/commands';
import { isAppleDevice } from 'utils/keyboard';
import { pubSub } from 'utils/pubSub';
import { useAppSelector } from 'utils/redux/useAppSelector';

export function useCommandKeybindings(): void {
  const { keybindings: commandsKeybindings, enrichedCommands } = useCommands();
  const { keyboardShortcuts, modals } = useAppSelector(
    (state) => ({
      keyboardShortcuts: state.settings.personal.keyboardShortcuts,
      modals: state.app.modals,
    }),
    shallowEqual,
  );

  const isRecordingModalVisible = !!(
    modals[0]?.type === 'app:keyCombinationRecorder'
  );

  useEffect(() => {
    if (!keyboardShortcuts) {
      return;
    }

    const keyBindingMap = enrichedCommands
      .filter(
        ({ isUserDefinedKeybindings, conflicts, command }) =>
          !command.isExternal &&
          (isUserDefinedKeybindings ||
            (!isUserDefinedKeybindings && conflicts.length === 0)) &&
          !!command.keyboard?.keybindings.length,
      )
      .reduce((keyBindingsMap, { command }) => {
        const { name: commandName, keyboard } = command;
        const commandKeyBindingsMap = Object.fromEntries(
          (keyboard?.keybindings ?? []).map((keybinding) => {
            const { binding, presentation } = keybinding;
            const keyCombination = binding.flat().join('+');
            const mods = binding[0];

            const handler = (event: KeyboardEvent) => {
              // Account for the possible target inside a Shadow DOM (e.g. in a web component),
              // in which case the event's target is the shadow root.
              // Fixes the behavior for the events originating from the BI app.
              const target = event.composedPath
                ? event.composedPath()[0]
                : event.target;

              const isContentEditableElement =
                target instanceof HTMLElement && target.isContentEditable;
              const isTargetAnInput =
                isContentEditableElement ||
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement;

              const isModsOnlyShift =
                mods.length === 1 && mods.includes('Shift');
              const isModsOptionAndShiftOnMac =
                isAppleDevice &&
                mods.length === 2 &&
                mods.includes('Alt') &&
                mods.includes('Shift');
              const isModsOnlyOptionOnMac =
                isAppleDevice && mods.length === 1 && mods.includes('Alt');

              const isKeybindingPreventingFromTyping =
                mods.length === 0 ||
                isModsOnlyShift ||
                isModsOptionAndShiftOnMac ||
                isModsOnlyOptionOnMac;

              if (
                (isKeybindingPreventingFromTyping && isTargetAnInput) ||
                isRecordingModalVisible
              ) {
                return;
              }

              event.preventDefault();

              ampli.commandExecuted({
                commandName,
                location: 'keyboard',
                keybinding: presentation
                  ? `${presentation.flat().join('+')} (${keyCombination})`
                  : keyCombination,
              });

              pubSub.publish(
                `${commandPubSubEventNamePrefix}${commandName}` as keyof CommandPubSubEvent,
              );
            };

            return [keyCombination, handler];
          }),
        );

        return Object.assign(keyBindingsMap, commandKeyBindingsMap);
      }, {} as KeyBindingMap);

    const keybindingsHandler = createKeybindingsHandler(keyBindingMap);

    addEventListener('keydown', keybindingsHandler);

    return () => {
      removeEventListener('keydown', keybindingsHandler);
    };
  }, [
    commandsKeybindings,
    enrichedCommands,
    isRecordingModalVisible,
    keyboardShortcuts,
  ]);
}
